import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { generalMessages } from 'src/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { ServicePlanPricingPolicyFormSchemaValues } from 'src/features/servicePlanPricingPolicies/utils/formSchema';
import { PAYMENT_TYPE, PRICING_MODE, PRICING_MODEL } from 'src/features/servicePlanPricingPolicies/utils/types';
import {
    PAYMENT_TYPE_MAP,
    PRICING_MODE_MAP,
    PRICING_MODEL_MAP,
} from 'src/features/servicePlanPricingPolicies/utils/constants';
import { useListReferenceRates } from 'src/features/referenceRates/api/useListReferenceRates';
import { useGetServicePlanItem } from 'src/features/servicePlanItems/api/useGetServicePlanItem';
import { useGetServicePlan } from 'src/features/servicePlans/api/useGetServicePlan';
import { useParams } from 'react-router-dom';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import { useListTaxReasons } from 'src/features/taxReasons/api/useListTaxReasons';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Input } from 'src/registry/ui/Input';
import React from 'react';
import { Calendar } from 'src/registry/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils';
import { PopoverPortal } from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import { REFERENCE_RATE_UNIT } from 'src/features/referenceRates/utils/types';
import { useFormatReferenceRateAmount } from 'src/features/referenceRates/hooks/useFormatReferenceRateAmount';

type ServicePlanPricingPolicyFormProps = {
    form: UseFormReturn<ServicePlanPricingPolicyFormSchemaValues>;
    onSubmit: (values: ServicePlanPricingPolicyFormSchemaValues) => void;
    /** False when the plan item carries no billable metric, which `per_unit` pricing requires. */
    itemHasMetric: boolean;
};

function ServicePlanPricingPolicyForm({ onSubmit, form, itemHasMetric }: ServicePlanPricingPolicyFormProps) {
    const { formatMessage, formatDate } = useIntl();
    const formatReferenceRateAmount = useFormatReferenceRateAmount();
    const { servicePlanItemId = '' } = useParams();
    const [displayAmount, setDisplayAmount] = React.useState('');
    const [displayPercent, setDisplayPercent] = React.useState('');
    const [openValidFromCalendar, setOpenValidFromCalendar] = React.useState<boolean>(false);
    const [openValidToCalendar, setOpenValidToCalendar] = React.useState<boolean>(false);

    const { data: taxReasonsData } = useListTaxReasons({});
    const { amount, pricing_mode, reference_rate } = form.watch();
    // Read during render so react-hook-form's formState proxy subscribes us to resets.
    const formDefaultValues = form.formState.defaultValues;

    const isIndexed = pricing_mode === PRICING_MODE.INDEXED;

    // A currency rate may only be indexed by a policy in the same currency, so currency rates are scoped to the
    // plan's currency rather than offering rates the backend would reject. Percent / coefficient / points rates
    // have no currency and fit any plan.
    const { data: servicePlanItem } = useGetServicePlanItem({
        id: servicePlanItemId,
        config: { enabled: !!servicePlanItemId && isIndexed },
    });
    const { data: servicePlan } = useGetServicePlan({
        id: servicePlanItem?.service_plan ?? '',
        config: { enabled: !!servicePlanItem?.service_plan && isIndexed },
    });
    const planCurrency = servicePlan?.current_terms?.currency;

    // Not filtered by `currency` on the server: that would also drop every rate whose currency is null.
    const { data: referenceRatesData } = useListReferenceRates({
        is_active: true,
        config: { enabled: isIndexed },
    });
    const referenceRates = React.useMemo(
        () =>
            (referenceRatesData?.results ?? []).filter(
                (rate) =>
                    (rate.unit ?? REFERENCE_RATE_UNIT.CURRENCY) !== REFERENCE_RATE_UNIT.CURRENCY ||
                    (!!planCurrency && rate.currency === planCurrency),
            ),
        [referenceRatesData, planCurrency],
    );
    const selectedReferenceRate = referenceRates.find((rate) => rate.id === reference_rate);

    React.useEffect(() => {
        if (amount != null) {
            setDisplayAmount(String(amount / 100));
        } else {
            setDisplayAmount('');
        }
    }, [amount]);

    /*
     * The API stores the multiplier as a decimal fraction ("0.5") but the input shows a percentage
     * (50). Syncing only on form reset keeps the display from being rewritten while typing.
     */
    React.useEffect(() => {
        const storedMultiplier = form.getValues('rate_multiplier');
        setDisplayPercent(storedMultiplier ? String(Number((Number(storedMultiplier) * 100).toFixed(4))) : '');
    }, [form, formDefaultValues]);

    const handleModeChange = (nextMode: PRICING_MODE) => {
        // Clear the side that is going away so a stale value can never reach the payload.
        if (nextMode === PRICING_MODE.FIXED) {
            form.setValue('reference_rate', null, { shouldDirty: true });
            form.setValue('rate_multiplier', null, { shouldDirty: true });
            setDisplayPercent('');
        } else {
            form.setValue('amount', null, { shouldDirty: true });
            setDisplayAmount('');
        }
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(generalMessages.validFrom)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openValidFromCalendar} onOpenChange={setOpenValidFromCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenValidFromCalendar(true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent
                                                className="z-50 max-h-(--radix-popover-content-available-height) overflow-y-auto bg-white p-4 shadow-sm"
                                                align="end"
                                            >
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenValidFromCalendar(false);
                                                        }
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => {
                    const selectedDate = field.value ? new Date(field.value) : undefined;

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(generalMessages.validTo)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Popover open={openValidToCalendar} onOpenChange={setOpenValidToCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                id="date"
                                                variant="outline"
                                                className="w-full justify-start text-base font-normal"
                                                onClick={() => setOpenValidToCalendar(true)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.CALENDAR}
                                                    className="text-primary-700 mr-1 size-4"
                                                />
                                                <span className={cn(!field.value && 'text-gray-500')}>
                                                    {field.value
                                                        ? formatDate(new Date(field.value))
                                                        : `${formatMessage(generalMessages.selectDate)}...`}
                                                </span>
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverPortal>
                                            <PopoverContent
                                                className="z-50 max-h-(--radix-popover-content-available-height) overflow-y-auto bg-white p-4 shadow-sm"
                                                align="end"
                                            >
                                                <Calendar
                                                    captionLayout="dropdown"
                                                    mode="single"
                                                    selected={selectedDate}
                                                    onSelect={(date) => {
                                                        if (date) {
                                                            field.onChange(dayjs(date).format('YYYY-MM-DD'));
                                                            setOpenValidToCalendar(false);
                                                        }
                                                    }}
                                                />
                                            </PopoverContent>
                                        </PopoverPortal>
                                    </Popover>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <SelectFormField<ServicePlanPricingPolicyFormSchemaValues>
                form={form}
                name="pricing_mode"
                required
                label={formatMessage(pricingPoliciesMessages.pricingMode)}
                options={Object.values(PRICING_MODE)}
                optionTranslationMap={PRICING_MODE_MAP}
                onChange={(value) => handleModeChange(value as PRICING_MODE)}
            />

            {!isIndexed && (
                <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...pricingPoliciesMessages.amount} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder={`${formatMessage(pricingPoliciesMessages.amount)}...`}
                                        value={displayAmount}
                                        onBlur={() => {
                                            if (!displayAmount) return;

                                            const numeric = parseFloat(displayAmount);
                                            if (!isNaN(numeric)) {
                                                setDisplayAmount(numeric.toFixed(2));
                                            }
                                        }}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;

                                            if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                            setDisplayAmount(inputValue);

                                            const numeric = parseFloat(inputValue);
                                            if (!isNaN(numeric)) {
                                                field.onChange(Math.round(numeric * 100));
                                            } else {
                                                field.onChange(null);
                                                setDisplayAmount('');
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}

            {isIndexed && (
                <>
                    <FormField
                        control={form.control}
                        name="reference_rate"
                        render={({ field }) => (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    <FormattedMessage {...pricingPoliciesMessages.referenceRate} />
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue
                                                    placeholder={`${formatMessage(pricingPoliciesMessages.referenceRate)}...`}
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {referenceRates.map((rate) => (
                                                    <SelectItem key={rate.id} value={rate.id}>
                                                        {rate.current_value
                                                            ? `${rate.name} (${rate.code}) \u2014 ${formatReferenceRateAmount(
                                                                  rate.current_value.amount,
                                                                  rate,
                                                              )}`
                                                            : `${rate.name} (${rate.code})`}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        )}
                    />
                    {!!selectedReferenceRate &&
                        (selectedReferenceRate.unit ?? REFERENCE_RATE_UNIT.CURRENCY) !==
                            REFERENCE_RATE_UNIT.CURRENCY && (
                            <FormDescription>
                                {formatMessage(pricingPoliciesMessages.nonCurrencyReferenceRateHint)}
                            </FormDescription>
                        )}
                    {!!referenceRatesData && referenceRates.length === 0 && (
                        <FormDescription>
                            {formatMessage(pricingPoliciesMessages.noReferenceRatesForCurrency)}
                        </FormDescription>
                    )}
                    {/*
                     * A rate with no value covering today cannot back a policy - warn here rather
                     * than letting the save fail with `missing_reference_rate_value`.
                     */}
                    {!!selectedReferenceRate && !selectedReferenceRate.current_value && (
                        <FormDescription className="text-red-500">
                            {formatMessage(pricingPoliciesMessages.referenceRateHasNoValueWarning)}
                        </FormDescription>
                    )}
                    <FormField
                        control={form.control}
                        name="rate_multiplier"
                        render={({ field }) => (
                            <FormItem variant="dialog">
                                <FormLabel variant="dialog" required>
                                    <FormattedMessage {...pricingPoliciesMessages.ratePercent} />
                                </FormLabel>
                                <FormControlStack>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="50"
                                            value={displayPercent}
                                            onChange={(e) => {
                                                const inputValue = e.target.value;

                                                if (!/^\d*\.?\d{0,4}$/.test(inputValue)) return;

                                                setDisplayPercent(inputValue);

                                                const numeric = parseFloat(inputValue);
                                                if (isNaN(numeric)) {
                                                    field.onChange(null);
                                                    return;
                                                }

                                                // Percent in, decimal fraction out: 50 -> "0.5".
                                                field.onChange(String(Number((numeric / 100).toFixed(6))));
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormControlStack>
                            </FormItem>
                        )}
                    />
                    <FormDescription>{formatMessage(pricingPoliciesMessages.ratePercentHint)}</FormDescription>
                </>
            )}

            <SelectFormField<ServicePlanPricingPolicyFormSchemaValues>
                form={form}
                name="payment_type"
                required
                label={formatMessage(pricingPoliciesMessages.paymentType)}
                options={Object.values(PAYMENT_TYPE)}
                optionTranslationMap={PAYMENT_TYPE_MAP}
            />
            <FormDescription>{formatMessage(pricingPoliciesMessages.paymentTypeHint)}</FormDescription>
            <SelectFormField<ServicePlanPricingPolicyFormSchemaValues>
                form={form}
                name="pricing_model"
                required
                label={formatMessage(pricingPoliciesMessages.pricingModel)}
                options={Object.values(PRICING_MODEL)}
                optionTranslationMap={PRICING_MODEL_MAP}
            />
            {form.watch('pricing_model') === PRICING_MODEL.PER_UNIT && !itemHasMetric && (
                <FormDescription>{formatMessage(pricingPoliciesMessages.perUnitRequiresMetric)}</FormDescription>
            )}
            <FormField
                control={form.control}
                name="free_quantities"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...pricingPoliciesMessages.freeQuantities} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    step={1}
                                    placeholder={`${formatMessage(pricingPoliciesMessages.freeQuantities)}...`}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        field.onChange(inputValue === '' ? null : Math.trunc(Number(inputValue)));
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="tax_reason"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...pricingPoliciesMessages.taxReason} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(pricingPoliciesMessages.taxReason)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {taxReasonsData?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default ServicePlanPricingPolicyForm;
